import { drawExtensionIcon } from '@/extensions/pixelIcons';
import { reaction } from 'mobx';
import { z } from 'zod';
import type { DigitilesExtension } from '../catalog';
import { watchActiveExtensionNodes, type ActiveExtensionNode } from '../activeNodes';
import { configSchema, fileSources, Reader, type Config, type Source } from './source';
import type { TileDefinition } from '@/models/tiles/TileDefinition';
import { standardBuildAndConnect } from '@/models/logic/utils';
import { CircuitType, Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { colors } from '@/styles';

export const FILE_PIN = 'extension:digitiles.binary-file/pin' as const;
export const CLOCK = 32,
  RESET = 33,
  EOF = 34,
  READY = 35;
const defaults = (): Config => ({
  source: 'unconfigured',
  width: 8,
  limit: 1024 * 1024,
  pin: 0,
});
const runtimeSchema = configSchema.extend({ value: z.boolean().default(false) });
const tile: TileDefinition<Config> = {
  type: FILE_PIN,
  name: 'Binary file chip pin (internal)',
  canRotate: true,
  stateSchema: configSchema,
  createDefaultState: defaults,
  graphConfiguration: (state) => state,
  ioSides: {
    [Side.LEFT]: { type: TileSideIOType.INPUT, forceConnection: true },
    [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
    [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
  },
  buildAndConnectNodes(data) {
    const result = standardBuildAndConnect(FILE_PIN, data, tile);
    const node = data.graph.nodes.get(data.instance.logicNodeIds!.right!);
    if (node) Object.assign(node.state, data.instance.state);
    return result;
  },
  graphics: {
    type: FILE_PIN,
    draw(g, { meta, connections, context }) {
      defaultTileGraphics(g);
      connectionGraphics(
        g,
        colors.signalColor,
        colors.noSignalColor,
        meta,
        connections,
        tile.ioSides,
        context,
      );
      drawExtensionIcon(g, 'file', 0x8bd6ff);
    },
  },
};
type Group = {
  targets: ActiveExtensionNode[];
  source?: Source;
  reader?: Reader;
  clock: boolean;
};
// Instance prefixes distinguish two placements of the same generated chip.
const groupKey = (target: ActiveExtensionNode) => {
  const key = target.node.runtimeKey ?? '';
  return `${key.slice(0, key.lastIndexOf('["tile",'))}|${target.node.state.source}`;
};

export const binaryFileExtension: DigitilesExtension = {
  id: 'digitiles.binary-file',
  name: 'Binary file reader',
  version: '1.0.0',
  description:
    'Create a chip with adjacent data pins, Clock, Reset, EOF and Ready. Read a chosen local file; no upload or file writing. Files stay in session memory and must be reselected after reload.',
  // Internal pins are registered for generated chips, not exposed as a palette UX.
  tiles: [{ definition: tile, circuits: [CircuitType.ChipLogic] }],
  nodes: [
    {
      type: FILE_PIN,
      stateSchema: runtimeSchema.transform((state) => ({ ...state, value: false })),
      configurationKeys: ['source', 'width', 'limit', 'pin'],
      createDefaultState: () => ({ ...defaults(), value: false }),
      process: (input, state) =>
        state.pin === CLOCK || state.pin === RESET ? input : state.value,
    },
  ],
  activate(app) {
    const sources = fileSources(app.context!);
    let groups = new Map<ActiveExtensionNode['simulation'], Map<string, Group>>();
    let stops: (() => void)[] = [];
    const publish = (group: Group) => {
      const changed: ActiveExtensionNode[] = [];
      for (const target of group.targets) {
        const pin = target.node.state.pin;
        if (pin === CLOCK || pin === RESET) continue;
        const reader = group.reader;
        const value =
          pin === READY
            ? !!reader
            : pin === EOF
              ? !!reader?.eof
              : !!reader && Math.floor(reader.word / 2 ** pin) % 2 === 1;
        if (target.node.state.value !== value || target.node.output !== value) {
          target.node.state.value = value;
          if (target.simulation.graph.nodes.get(target.node.id) === target.node)
            changed.push(target);
        }
      }
      // Publish a complete word/EOF/Ready snapshot before scheduling propagation.
      for (const target of changed) target.simulation.dirtyNodes.add(target.node.id);
      if (changed.length) changed[0].simulation.triggerUpdate(changed[0].node.id);
    };
    const refresh = () => {
      for (const byKey of groups.values())
        for (const group of byKey.values()) {
          const state = group.targets[0].node.state as Config;
          const source = sources.entries.get(state.source);
          if (source !== group.source) {
            group.source = source;
            group.reader = source
              ? new Reader(source.bytes.subarray(0, state.limit), state.width)
              : undefined;
          }
          publish(group);
        }
    };
    const unwatch = watchActiveExtensionNodes(app, FILE_PIN, (targets) => {
      stops.forEach((stop) => stop());
      stops = [];
      const next = new Map<ActiveExtensionNode['simulation'], Map<string, Group>>();
      for (const target of targets) {
        let byKey = next.get(target.simulation);
        if (!byKey) next.set(target.simulation, (byKey = new Map()));
        const key = groupKey(target);
        let group = byKey.get(key);
        if (!group) {
          const previous = groups.get(target.simulation)?.get(key);
          byKey.set(
            key,
            (group = { ...previous, targets: [], clock: previous?.clock ?? false }),
          );
        }
        group.targets.push(target);
      }
      for (const [simulation, byKey] of groups)
        for (const [key, group] of byKey) {
          if (!next.get(simulation)?.has(key)) {
            group.reader = undefined;
            publish(group);
          }
        }
      groups = next;
      refresh();
      for (const [simulation, byKey] of groups) {
        for (const group of byKey.values()) {
          const clock = group.targets.find((t) => t.node.state.pin === CLOCK);
          group.clock = clock ? clock.graph.getNodeInput(clock.node.id) : false;
        }
        stops.push(
          simulation.subscribeProcessed((ids) => {
            for (const group of byKey.values()) {
              const clock = group.targets.find((t) => t.node.state.pin === CLOCK);
              const reset = group.targets.find((t) => t.node.state.pin === RESET);
              if (
                !clock ||
                (!ids.has(clock.node.id) && (!reset || !ids.has(reset.node.id)))
              )
                continue;
              const high = clock.graph.getNodeInput(clock.node.id);
              if (reset && reset.graph.getNodeInput(reset.node.id)) group.reader?.reset();
              else if (high && !group.clock) group.reader?.advance();
              group.clock = high;
              publish(group);
            }
          }),
        );
      }
    });
    const unsource = reaction(() => [...sources.entries.values()], refresh);
    return () => {
      unsource();
      unwatch();
      stops.forEach((stop) => stop());
    };
  },
};
